import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { memo } from 'react';
import { StudentQuestionType } from '../../../api/student-question';
import { useBooleanState } from '../../../hooks/index/use-boolean-state';
import { useUpdateStudentQuestionMarkedMutation } from '../../../hooks/react-query/use-student-question';
import Button from '../../shared/buttons/button';
import Segment from '../../shared/segment';
import QuestionOptions from './question-options';
import QuestionTitle from './question-title';

const STATUS_COLORS = ['red', 'yellow', 'green'] as const;

export type StudentQuestionPropsType = {
  studentQuestion: StudentQuestionType;
  questionNumber: number;
  questionCount: number;
  showAllAnswers: boolean;
  showLessonName: boolean;
};

function StudentQuestion({
  studentQuestion,
  questionNumber,
  questionCount,
  showAllAnswers,
  showLessonName
}: StudentQuestionPropsType) {
  const { value: isAnswerRevealed, setTrue: revealAnswer } = useBooleanState(false);
  const { mutate: updateMarked, isLoading: isUpdatingMarked } =
    useUpdateStudentQuestionMarkedMutation();

  const isAnswerVisible = isAnswerRevealed || showAllAnswers;
  const segmentColor = isAnswerVisible ? STATUS_COLORS[studentQuestion.status + 1] : 'white';

  const handleBookmarkToggle = () => {
    if (isUpdatingMarked) return;
    updateMarked({
      studentQuestionId: studentQuestion.id,
      marked: !studentQuestion.marked
    });
  };

  return (
    <Segment noMargin color={segmentColor} className='my-1'>
      <div className='flex justify-between'>
        <QuestionTitle questionNumber={questionNumber} questionCount={questionCount} />

        <button
          type='button'
          onClick={handleBookmarkToggle}
          disabled={isUpdatingMarked}
          aria-pressed={studentQuestion.marked}
          aria-label={studentQuestion.marked ? 'حذف نشان' : 'نشان کردن'}
          className='cursor-pointer disabled:cursor-not-allowed disabled:opacity-60'
        >
          {studentQuestion.marked ? (
            <BookmarkSolidIcon width='25px' className='text-amber-400' />
          ) : (
            <BookmarkOutlineIcon width='25px' className='text-amber-400' />
          )}
        </button>
      </div>
      <div className='mx-3 flex flex-col gap-2 py-3'>
        <div
          dangerouslySetInnerHTML={{ __html: studentQuestion.question.question }}
          className='ck-content'
        />
        {showLessonName && (
          <span
            className='inline-flex items-center px-1 text-[10px]
           font-bold uppercase tracking-wider text-gray-400'
          >
            {studentQuestion.exam.name}
          </span>
        )}
        <div className='my-2'>
          <QuestionOptions
            options={studentQuestion.question.options}
            selectedOptionId={studentQuestion.option}
            showAnswer={isAnswerVisible}
          />
        </div>
      </div>

      {isAnswerVisible ? (
        <div className='flex flex-col gap-2'>
          <p className='text-lg font-medium'>جواب:</p>
          <div
            dangerouslySetInnerHTML={{ __html: studentQuestion.question.answer }}
            className='ck-content mx-3'
          />
        </div>
      ) : (
        <div className='my-3'>
          <Button content='دیدن جواب' onClick={revealAnswer} className='mx-auto w-2/3' />
        </div>
      )}
    </Segment>
  );
}

export default memo(StudentQuestion);
